import { Experience } from '../types';
import { HourlyRateRow } from '@/interfaces/fees';
import { Proposal } from '@/interfaces/types';
import { v4 as uuidv4 } from 'uuid';
import { FEE_ACTIONS, handleFeeAction } from './feeReducer';
import { defaultFeeTextEn, defaultFeeTextPl } from '../PostProposalGeneration/SecondTab/defaultFeeText';

export const PROPOSALACTIONS = {
    SET_FIELD: 'set_field',
    INITIALIZE: 'initialize',
    SET_INTRO_DEFAULT: 'set_intro_default',
    SET_CONSTRAINTS: 'set_constraints',
    ADD_LAWYER_CONSTRAINT: 'add_lawyer_constraint',
    REMOVE_LAWYER_CONSTRAINT: 'remove_lawyer_constraint',
    ADD_SENIORITY_CONSTRAINT: 'add_seniority_constraint',
    REMOVE_SENIORITY_CONSTRAINT: 'remove_seniority_constraint',
    ADD_TEAM_CONSTRAINT: 'add_team_constraint',
    REMOVE_TEAM_CONSTRAINT: 'remove_team_constraint',
    ADD_N_EXPERIENCE_CONSTRAINT: 'add_n_experience_constraint',
    ADD_REPRESENTATIVE: 'add_representative',
    REMOVE_REPRESENTATIVE: 'remove_representative',
    ADD_LAWYER: 'add_lawyer',
    REMOVE_LAWYER: 'remove_lawyer',
    EDIT_LAWYER: 'edit_lawyer',
    EDIT_LAWYER_BIO: 'edit_lawyer_bio',
    REORDER_LAWYERS: 'reorder_lawyers',
    ADD_EXPERIENCE: 'add_experience',
    REMOVE_EXPERIENCE: 'remove_experience',
    EDIT_EXPERIENCE: 'edit_experience',
    REORDER_EXPERIENCES: 'reorder_experiences',
    SORT_LAWYERS_BY_SENIORITY: 'sort_lawyers_by_seniority',
} as const;

export const introLetterEng = `
Dear _____

thank you for inviting the law firm WKB Wierciński, Kwieciński, Baehr ("WKB") to present an offer of legal assistance for _____.

In response to your request, below we provide information about the firm, details related to the scope of our advice in this proposal and present our previous experience and the team of lawyers dedicated to the assignment.

In our opinion, the knowledge and experience of our team members ensure that WKB can offer you comprehensive legal services at the highest level. We hope that our offer will meet your expectations. 

If you have any question, do not hesitate to contact us. We will be very happy to answer any of them you may have.

Kind regards,
`;

export const introLetterPol = `
Szanowna Pani/Panie _____

dziękujemy za zaproszenie kancelarii WKB Wierciński, Kwieciński, Baehr sp. k. („WKB”) do przedstawienia oferty współpracy na świadczenie usług w zakresie […]

W odpowiedzi na Państwa zapytanie, poniżej przedstawiamy informacje o kancelarii, szczegóły związane z zakresem naszego doradztwa w ramach niniejszej oferty oraz prezentujemy nasze dotychczasowe doświadczenie i zespół prawników dedykowanych do realizacji zlecenia.

W naszej ocenie wiedza i doświadczenie członków naszego zespołu sprawia, że WKB może zaoferować Państwu kompleksową obsługę prawną na najwyższym poziomie. Mamy nadzieję, że nasza oferta spełni Państwa oczekiwania.

W przypadku jakichkolwiek pytań, uprzejmie prosimy o kontakt. Bardzo chętnie odpowiemy na wszelkie Państwa pytania.

Z poważaniem`;


// Initial state structure
export const initialProposalState: Proposal = {
    id: '',
    title: 'New Proposal',
    input: '',
    sector: '',
    constraints: {
        teamComposition: [],
        requiredLawyers: [],
        nExperiences: 0
    },
    client: { name: '', company: '' },
    intro: introLetterPol,
    scope: '',
    assumptions: '',
    fees: defaultFeeTextPl,
    lawyers: [] as { name: string; bio: string; position?: string }[],
    experience: [] as Experience[],
    proposalDate: new Date(),
    // specialRoles: {},
    language: 'pl',
    letterSenders: []
}

export function proposalReducer(state: Proposal, action: any) {
    // First check if it's a fee-related action
    if (Object.values(FEE_ACTIONS).includes(action.type)) {
        return handleFeeAction(state, action);
    }

    switch (action.type) {
        case PROPOSALACTIONS.SET_FIELD:
            return { ...state, [action.field]: action.value };

        case PROPOSALACTIONS.INITIALIZE:
            const payload = action.payload;
            const modifiedPayload = {
                id: payload.id,
                title: payload.title,
                input: payload.input,
                sector: payload.sector,
                constraints: payload.constraints,
                client: payload.client,
                intro: payload.intro,
                letterSenders: payload.letterSenders || [],
                scope: payload.scope,
                assumptions: payload.assumptions,
                fees: payload.fees || [],
                lawyers: payload.lawyers || {},
                experience: payload.experience || [],
                proposalDate: new Date(payload.proposalDate),
                displayId: payload.displayId,
                // specialRoles: payload.specialRoles,
                language: payload.language,
            }

            return {
                ...state,
                ...modifiedPayload
            };

        case PROPOSALACTIONS.SET_CONSTRAINTS:
            return { ...state, constraints: { ...state.constraints, [action.field]: action.value } };

        case PROPOSALACTIONS.ADD_TEAM_CONSTRAINT:
            return {
                ...state,
                constraints: {
                    ...state.constraints,
                    teamComposition: (state.constraints &&
                        state.constraints.teamComposition && [
                            ...state.constraints.teamComposition,
                            action.payload,
                        ]) || [action.payload],
                },
            };

        case PROPOSALACTIONS.REMOVE_TEAM_CONSTRAINT:
            return {
                ...state,
                constraints: {
                    ...state.constraints,
                    teamComposition:
                        (state.constraints &&
                            state.constraints.teamComposition &&
                            state.constraints.teamComposition.filter((team) => team !== action.payload)) ||
                        [],
                },
            };


        case PROPOSALACTIONS.ADD_N_EXPERIENCE_CONSTRAINT:
            return {
                ...state,
                constraints: {
                    ...state.constraints,
                    nExperiences: action.payload,
                },
            };

        // Dealing with Lawyers
        case PROPOSALACTIONS.ADD_LAWYER:
            if (state.lawyers.map((lawyer) => lawyer.name).includes(action.payload.name)) {
                return state;
            } else {
                return {
                    ...state,
                    lawyers: [
                        ...state.lawyers,
                        {
                            name: action.payload.name,
                            bio: action.payload.bio,
                        },
                    ],
                };
            }

        case PROPOSALACTIONS.REMOVE_LAWYER:
            let newLawyers = [...state.lawyers];
            const remove_index = newLawyers.findIndex((lawyer) => lawyer.name === action.payload);
            newLawyers.splice(remove_index, 1);
            return { ...state, lawyers: newLawyers };

        case PROPOSALACTIONS.EDIT_LAWYER:
            const { oldName, newName } = action.payload;
            const lawyerIndex2 = state.lawyers.findIndex((lawyer) => lawyer.name === oldName);
            const newLawyers2 = [...state.lawyers];
            newLawyers2[lawyerIndex2] = { name: newName, bio: '' };
            return { ...state, lawyers: newLawyers2 }


        case PROPOSALACTIONS.EDIT_LAWYER_BIO:
            const { name, bio } = action.payload;
            const lawyerIndex = state.lawyers.findIndex((lawyer) => lawyer.name === name);
            const newLawyers1 = [...state.lawyers];
            newLawyers1[lawyerIndex] = { name, bio: bio };

            return {
                ...state,
                lawyers: newLawyers1,
            };

        case PROPOSALACTIONS.REORDER_LAWYERS:
            return {
                ...state,
                lawyers: action.payload,
            }


        // Dealing with experiences
        case PROPOSALACTIONS.ADD_EXPERIENCE:
            if (action.payload) {
                let { title, description, client, team } = action.payload;
                // Check if an experience with the same client name already exists
                if (state.experience.some(exp => exp.client === client)) {
                    console.log('Duplicate client was attempted to be added to experience list:', client);
                    return state; // Return unchanged state if duplicate client found
                }
                return {
                    ...state,
                    experience: [
                        ...state.experience,
                        {
                            title: title || '',
                            description: description || '',
                            client: client || '',
                            team: team || '',
                        },
                    ],
                };
            } else {
                return {
                    ...state,
                    experience: [...state.experience, { title: '', description: '', client: '', team: '' }],
                };
            }

        case PROPOSALACTIONS.EDIT_EXPERIENCE:
            const { title, description, client, team, idx } = action.payload;
            if (idx === undefined) {
                console.log('ERROR: No index provided for experience edit');
                return state;
            }
            const updatedExperience = structuredClone(state.experience);
            updatedExperience[idx] = {
                title: title || state.experience[idx].title,
                description: description || state.experience[idx].description,
                client: client || state.experience[idx].client,
                team: team || state.experience[idx].team,
            };
            return { ...state, experience: updatedExperience };

        case PROPOSALACTIONS.REMOVE_EXPERIENCE:
            const index = action.payload;
            return { ...state, experience: state.experience.filter((_, i) => i !== index) };

        case PROPOSALACTIONS.REORDER_EXPERIENCES:
            return {
                ...state,
                experience: action.payload,
            }

        case PROPOSALACTIONS.SORT_LAWYERS_BY_SENIORITY:
            const seniorityOrder = [
                'Managing Partner',
                'Partner',
                'International Partner',
                'Managing Associate',
                'Counsel',
                'Senior Associate',
                'Associate',
                'Junior Associate',
                'Paralegal'
            ];

            const sortedLawyers = [...state.lawyers].sort((a, b) => {
                const aPosition = action.payload.lawyerPositions[a.name]?.PositionEn || '';
                const bPosition = action.payload.lawyerPositions[b.name]?.PositionEn || '';
                const aIndex = seniorityOrder.indexOf(aPosition);
                const bIndex = seniorityOrder.indexOf(bPosition);
                return aIndex - bIndex;
            });

            return {
                ...state,
                lawyers: sortedLawyers
            };

        default:
            return state;
    }
}


